import {HStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {KeyboardEvent, useRef, useState} from 'react';

import {append, without} from 'ramda';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {ColorChip} from './ColorChip';
import {ColorChipsOption} from './ColorChipsOption';

export interface ColorChipsProps extends FormControlProps<string[] | Nullish>, TestIdProps {
  options: ColorChipsOption[];
  isDeselectable?: boolean;
}

export function ColorChips(props: ColorChipsProps) {
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [isKeyboardNav, setIsKeyboardNav] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleChange = (selectedValue: string) => {
    const isSelected = !!props.value?.includes(selectedValue);

    const nextValue = !isSelected
      ? append(selectedValue, props.value ?? [])
      : props.isDeselectable !== false
        ? without([selectedValue], props.value ?? [])
        : (props.value ?? []);

    props.onChange?.(nextValue);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (props.isDisabled || props.options.length === 0) {
      return;
    }

    const lastIndex = props.options.length - 1;

    match(event.key)
      .with('ArrowRight', 'ArrowDown', () => {
        event.preventDefault();
        setActiveIndex((prev) => (prev >= lastIndex ? 0 : prev + 1));
      })
      .with('ArrowLeft', 'ArrowUp', () => {
        event.preventDefault();
        setActiveIndex((prev) => (prev <= 0 ? lastIndex : prev - 1));
      })
      .with(' ', 'Enter', () => {
        event.preventDefault();
        if (activeIndex >= 0 && activeIndex <= lastIndex) {
          handleChange(props.options[activeIndex].value);
        }
      })
      .with('Home', () => {
        event.preventDefault();
        setActiveIndex(0);
      })
      .with('End', () => {
        event.preventDefault();
        setActiveIndex(lastIndex);
      })
      .otherwise(() => {});
  };

  return (
    <div
      ref={wrapperRef}
      tabIndex={props.isDisabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      onFocus={(event) => {
        if (event.target.matches?.(':focus-visible')) {
          setIsKeyboardNav(true);
          if (activeIndex === -1) {
            setActiveIndex(0);
          }
        }
      }}
      onBlur={() => setActiveIndex(-1)}
      css="&:focus { outline: none; }"
    >
      <HStack spacing={2} align="center" wrap data-testid={suffixTestId('colorChips', props)}>
        {props.options.map((option, index) => (
          <ColorChip
            key={option.value}
            label={option.label}
            color={option.color}
            isDisabled={props.isDisabled}
            value={!!props.value?.includes(option.value)}
            onChange={() => {
              setActiveIndex(index);
              handleChange(option.value);
            }}
            isFocused={index === activeIndex && isKeyboardNav}
            data-testid={suffixTestId(`${index}`, props)}
          />
        ))}
      </HStack>
    </div>
  );
}
